'use client';

import { useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LandingHeader, LandingFooter } from '../../../features/landing';
import type { ComparisonMeta } from './lib/comparisons';

interface CompareListContentProps {
  comparisons: ComparisonMeta[];
}

export function CompareListContent({ comparisons }: CompareListContentProps) {
  const t = useTranslations('creator.compare');
  const locale = useLocale();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}
        >
          {t('pageTitle')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 5, textAlign: 'center', maxWidth: 600, mx: 'auto' }}
        >
          {t('compareDescription')}
        </Typography>

        {comparisons.length === 0 ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 8 }}
          >
            {t('pageDescription')}
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {comparisons.map((comparison) => (
              <Grid key={comparison.slug} size={{ xs: 12, sm: 6, md: 4 }}>
                <ComparisonCard comparison={comparison} locale={locale} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <LandingFooter />
    </Box>
  );
}

function ComparisonCard({
  comparison,
  locale,
}: {
  comparison: ComparisonMeta;
  locale: string;
}) {
  const t = useTranslations('creator.compare');

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
    >
      <CardActionArea
        component={NextLink}
        href={`/${locale}/compare/${comparison.slug}`}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CompareArrowsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Chip
              label={comparison.competitorName}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 1,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {comparison.headline}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {comparison.description}
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            sx={{ color: 'text.disabled', fontSize: '0.8rem' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">
                Boty: {comparison.setupTimeBoty}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">
                {comparison.competitorName}: {comparison.setupTimeCompetitor}
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="primary"
            sx={{ mt: 2, fontWeight: 600 }}
          >
            {t('readComparison')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
